import type { Rect } from './climb-thumbnail';

/**
 * **The shapes a shared picture or video is made in**, and how the media is
 * framed in them.
 *
 * Every format is 1080 px wide, so a card's or a mark's place (kept as
 * fractions of the frame) means the same width in all of them; only the height
 * changes. The media starts with its whole width across the frame
 * (`fitWidthFraming`), and is moved and zoomed in it with the fingers —
 * smaller than the frame too, as on Instagram.
 */
export type ShareFormat = 'reel' | 'story' | 'post' | 'square';

export const SHARE_FORMATS: ShareFormat[] = ['reel', 'story', 'post', 'square'];

export const FORMAT_SIZE: Record<ShareFormat, { width: number; height: number }> = {
  reel: { width: 1080, height: 1920 },
  story: { width: 1080, height: 1920 },
  post: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
};

/** A format's name, in the dialog it is picked from. */
export const FORMAT_NAME_KEYS: Record<ShareFormat, string> = {
  reel: 'shareClimb.formatReel',
  story: 'shareClimb.formatStory',
  post: 'shareClimb.formatPost',
  square: 'shareClimb.formatSquare',
};

/**
 * What Instagram's own interface covers, in pixels of the 1080 px wide frame:
 * the profile and the close button at the top of a story, the reply bar at
 * its bottom; a reel's caption and audio line at the bottom and its column of
 * buttons on the right. **Meta publishes no figures for organic posts**: these
 * are the ones third-party guides agree on most (checked 2026-10-30 —
 * socialync.io, revid.ai, hopperhq.com, moda.app), so they are an estimate,
 * kept here to be corrected in one place. Feed posts have nothing over them.
 */
const COVERED: Record<ShareFormat, { top: number; right: number; bottom: number; left: number }> = {
  story: { top: 250, right: 65, bottom: 340, left: 65 },
  reel: { top: 250, right: 120, bottom: 420, left: 65 },
  post: { top: 0, right: 0, bottom: 0, left: 0 },
  square: { top: 0, right: 0, bottom: 0, left: 0 },
};

/** Whether the format has parts Instagram covers, which the editor then shows. */
export function hasCoveredZones(format: ShareFormat): boolean {
  const c = COVERED[format];
  return c.top + c.right + c.bottom + c.left > 0;
}

/** The part of a frame of this format nothing of Instagram's covers: where the card and the mark may go. */
export function safeArea(format: ShareFormat, frameWidth: number, frameHeight: number): Rect {
  const scale = frameWidth / FORMAT_SIZE[format].width;
  const c = COVERED[format];
  return {
    x: c.left * scale,
    y: c.top * scale,
    width: frameWidth - (c.left + c.right) * scale,
    height: frameHeight - (c.top + c.bottom) * scale,
  };
}

/** The format a media starts in: a reel for a portrait video, a feed post for a picture, a square otherwise. */
export function defaultFormat(mediaType: 'image' | 'video', width: number, height: number): ShareFormat {
  if (mediaType === 'image') return 'post';
  return height >= width * 1.3 ? 'reel' : 'square';
}

// ── Framing ────────────────────────────────────────────────────────────────────

/**
 * How the media sits in the frame, as on Instagram: `zoom` 1 just covers the
 * frame, more goes in, less shows it smaller than the frame (on black);
 * (`cx`, `cy`) is the point of the media at the middle of the frame, as
 * fractions of the media. Fractions, so a framing holds across formats.
 *
 * A media at least as big as the frame always covers it; a smaller one stays
 * whole inside it — each way along each axis.
 */
export interface Framing {
  zoom: number;
  cx: number;
  cy: number;
}

export const DEFAULT_FRAMING: Framing = { zoom: 1, cx: 0.5, cy: 0.5 };

/** As far as the media may be zoomed into. */
export const MAX_ZOOM = 5;

/** As small as the media may be made: half of fitting the frame whole. */
const SMALLEST_SHARE_OF_FIT = 0.5;

/** What the frame is filled with where the media does not reach. */
export const FRAME_BACKGROUND = '#000000';

function coverScale(mediaWidth: number, mediaHeight: number, frameWidth: number, frameHeight: number): number {
  return Math.max(frameWidth / mediaWidth, frameHeight / mediaHeight);
}

/**
 * Where a media starts: its whole width across the frame's, in the middle. A
 * landscape clip in a reel is seen whole, on black above and below; a media
 * narrower than the frame fills it, cut a little at the top and the bottom.
 */
export function fitWidthFraming(mediaWidth: number, mediaHeight: number, frameWidth: number, frameHeight: number): Framing {
  const zoom = frameWidth / mediaWidth / coverScale(mediaWidth, mediaHeight, frameWidth, frameHeight);
  return { zoom, cx: 0.5, cy: 0.5 };
}

/** The smallest zoom for this media in this frame: below 1, as it is shown smaller than the frame. */
export function minZoom(mediaWidth: number, mediaHeight: number, frameWidth: number, frameHeight: number): number {
  const fit = Math.min(frameWidth / mediaWidth, frameHeight / mediaHeight);
  return (SMALLEST_SHARE_OF_FIT * fit) / coverScale(mediaWidth, mediaHeight, frameWidth, frameHeight);
}

/** One axis of the media's box: where it starts in the frame, kept covering it or inside it. */
function placeAlong(centre: number, mediaSize: number, frameSize: number): number {
  const start = frameSize / 2 - centre * mediaSize;
  return mediaSize >= frameSize
    ? Math.min(0, Math.max(frameSize - mediaSize, start))
    : Math.min(frameSize - mediaSize, Math.max(0, start));
}

/** Where the media is drawn in the frame, in the frame's pixels: past its edges when zoomed in. */
export function framedRect(mediaWidth: number, mediaHeight: number, frameWidth: number, frameHeight: number, framing: Framing): Rect {
  const scale = coverScale(mediaWidth, mediaHeight, frameWidth, frameHeight) * framing.zoom;
  const width = mediaWidth * scale;
  const height = mediaHeight * scale;
  return {
    x: placeAlong(framing.cx, width, frameWidth),
    y: placeAlong(framing.cy, height, frameHeight),
    width,
    height,
  };
}

/** A framing whose middle point is where the media really is (after the clamping along each axis). */
function settled(framing: Framing, mediaWidth: number, mediaHeight: number, frameWidth: number, frameHeight: number): Framing {
  const rect = framedRect(mediaWidth, mediaHeight, frameWidth, frameHeight, framing);
  return {
    zoom: framing.zoom,
    cx: (frameWidth / 2 - rect.x) / rect.width,
    cy: (frameHeight / 2 - rect.y) / rect.height,
  };
}

/** The media moved with a finger by (dx, dy), in the frame's pixels: it follows the finger. */
export function panFraming(
  framing: Framing,
  dx: number,
  dy: number,
  mediaWidth: number,
  mediaHeight: number,
  frameWidth: number,
  frameHeight: number,
): Framing {
  const rect = framedRect(mediaWidth, mediaHeight, frameWidth, frameHeight, framing);
  return settled(
    { ...framing, cx: (frameWidth / 2 - rect.x - dx) / rect.width, cy: (frameHeight / 2 - rect.y - dy) / rect.height },
    mediaWidth,
    mediaHeight,
    frameWidth,
    frameHeight,
  );
}

/** The media zoomed to `zoom`, about the middle of the frame, between `minZoom` and MAX_ZOOM. */
export function zoomFraming(
  framing: Framing,
  zoom: number,
  mediaWidth: number,
  mediaHeight: number,
  frameWidth: number,
  frameHeight: number,
): Framing {
  const clamped = Math.min(MAX_ZOOM, Math.max(minZoom(mediaWidth, mediaHeight, frameWidth, frameHeight), zoom));
  return settled({ ...framing, zoom: clamped }, mediaWidth, mediaHeight, frameWidth, frameHeight);
}

/** Draws part of a picture or a video frame: `drawImage`'s nine arguments, whatever the source. */
export type DrawSourceRect = (
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number, sw: number, sh: number,
  dx: number, dy: number, dw: number, dh: number,
) => void;

/** The frame filled, and the media drawn into it as the framing has it. */
export function drawFramed(
  ctx: CanvasRenderingContext2D,
  draw: DrawSourceRect,
  mediaWidth: number,
  mediaHeight: number,
  frameWidth: number,
  frameHeight: number,
  framing: Framing,
) {
  const rect = framedRect(mediaWidth, mediaHeight, frameWidth, frameHeight, framing);
  ctx.save();
  ctx.fillStyle = FRAME_BACKGROUND;
  ctx.fillRect(0, 0, frameWidth, frameHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  draw(ctx, 0, 0, mediaWidth, mediaHeight, rect.x, rect.y, rect.width, rect.height);
  ctx.restore();
}

/** `drawImage` from a picture or a <video>, as a `DrawSourceRect`. */
export function drawFromImage(source: CanvasImageSource): DrawSourceRect {
  return (ctx, sx, sy, sw, sh, dx, dy, dw, dh) => ctx.drawImage(source, sx, sy, sw, sh, dx, dy, dw, dh);
}
